import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import type { Permission } from "@/modules/permissions";
import type { Role } from "@/modules/roles";
import styles from "./role-form-dialog.module.scss";

export const CREATE_ROLE_TITLE = "Novo Perfil";
export const EDIT_ROLE_TITLE = "Editar Perfil";
export const FIELD_REQUIRED_MESSAGE = "Campo obrigatório";
export const NAME_PLACEHOLDER = "ex.: Gestor";

export const PERMISSIONS_LOADING_MESSAGE = "Carregando permissões...";
export const PERMISSIONS_ERROR_MESSAGE = "Não foi possível carregar as permissões.";
export const PERMISSIONS_EMPTY_MESSAGE = "Nenhuma permissão cadastrada.";

/** Liga o botão Salvar, que vive no rodapé do Dialog, ao formulário do corpo. */
const FORM_ID = "role-form";

/** Limite da coluna `nome` de `plataforma.perfil`. */
const NAME_MAX_LENGTH = 150;

export interface RoleFormValues {
  nome: string;
  /** RN02: conjunto completo marcado; o serviço sincroniza as associações. */
  permissaoIds: string[];
}

interface RoleFormDialogProps {
  /** Ausente na inclusão; presente na edição, com o nome e as permissões atuais. */
  role?: Role | null;
  permissions: Permission[];
  isLoadingPermissions: boolean;
  hasPermissionsError: boolean;
  /**
   * Persiste o perfil. Cabe ao chamador comunicar o resultado e fechar o modal
   * no sucesso — em caso de falha o modal segue aberto com os dados digitados.
   * A promessa não deve ser rejeitada.
   */
  onSubmit: (values: RoleFormValues) => Promise<void>;
  onCancel: () => void;
}

function permissionLabel(permission: Permission): string {
  return permission.nome || permission.codigo;
}

/**
 * Chaves das permissões já associadas ao perfil. Guarda id e código porque a
 * listagem pode devolver a permissão sem identificador, caso em que o gateway
 * usa o código no lugar.
 */
function initialSelection(role: Role | null | undefined): Set<string> {
  const keys = new Set<string>();

  for (const permission of role?.permissoes ?? []) {
    if (permission.id) keys.add(permission.id);
    if (permission.codigo) keys.add(permission.codigo);
  }

  return keys;
}

export function RoleFormDialog({
  role,
  permissions,
  isLoadingPermissions,
  hasPermissionsError,
  onSubmit,
  onCancel,
}: RoleFormDialogProps) {
  const [nome, setNome] = useState(role?.nome ?? "");
  const [selected, setSelected] = useState(() => initialSelection(role));
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = Boolean(role);
  // Sem o catálogo completo não dá para montar `permissaoIds`, e gravar apagaria
  // silenciosamente as associações existentes (RN02).
  const isCatalogReady = !isLoadingPermissions && !hasPermissionsError;

  function isChecked(permission: Permission): boolean {
    return selected.has(permission.id) || (!!permission.codigo && selected.has(permission.codigo));
  }

  function togglePermission(permission: Permission) {
    setSelected((previous) => {
      const next = new Set(previous);

      if (isChecked(permission)) {
        next.delete(permission.id);
        if (permission.codigo) next.delete(permission.codigo);
      } else {
        next.add(permission.id);
      }

      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmed = nome.trim();
    if (!trimmed) {
      // AC3: nada é gravado e as permissões marcadas seguem preservadas.
      setNameError(FIELD_REQUIRED_MESSAGE);
      return;
    }

    setNameError(null);
    setIsSubmitting(true);
    try {
      await onSubmit({
        nome: trimmed,
        permissaoIds: permissions.filter(isChecked).map((permission) => permission.id),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderPermissionsState(message: string, testId: string) {
    return (
      <p className={styles.permissionsState} role="status" data-testid={testId}>
        {message}
      </p>
    );
  }

  function renderPermissions() {
    if (isLoadingPermissions) {
      return renderPermissionsState(PERMISSIONS_LOADING_MESSAGE, "role-form-permissions-loading");
    }

    if (hasPermissionsError) {
      return renderPermissionsState(PERMISSIONS_ERROR_MESSAGE, "role-form-permissions-error");
    }

    if (permissions.length === 0) {
      return renderPermissionsState(PERMISSIONS_EMPTY_MESSAGE, "role-form-permissions-empty");
    }

    return (
      <div className={styles.permissionsScroll}>
        <table className={styles.permissionsTable} data-testid="role-form-permissions">
          <thead>
            <tr>
              <th scope="col" className={styles.selectColumn}>
                <span className={styles.srOnly}>Selecionar</span>
              </th>
              <th scope="col">Nome</th>
              <th scope="col">Descrição</th>
            </tr>
          </thead>
          <tbody>
            {permissions.map((permission) => (
              <tr key={permission.id}>
                <td className={styles.selectColumn}>
                  <Checkbox
                    inputId={`role-form-permission-${permission.id}`}
                    checked={isChecked(permission)}
                    onChange={() => togglePermission(permission)}
                    disabled={isSubmitting}
                    aria-label={permissionLabel(permission)}
                    data-testid={`role-form-permission-${permission.id}`}
                  />
                </td>
                <td className={styles.permissionName}>{permissionLabel(permission)}</td>
                <td className={styles.permissionDescription}>{permission.descricao}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // O rodapé é uma faixa própria do Dialog, portanto fica fora do <form>:
  // o atributo `form` mantém Salvar como botão de envio do formulário.
  const footer = (
    <div className={styles.actions}>
      <Button
        type="button"
        className={styles.cancelButton}
        label="Cancelar"
        severity="secondary"
        text
        onClick={onCancel}
        disabled={isSubmitting}
        data-testid="role-form-cancel"
      />
      <Button
        type="submit"
        form={FORM_ID}
        className={styles.saveButton}
        label="Salvar"
        loading={isSubmitting}
        disabled={!isCatalogReady}
        data-testid="role-form-save"
      />
    </div>
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      header={isEditing ? EDIT_ROLE_TITLE : CREATE_ROLE_TITLE}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="role-form-dialog"
    >
      <form id={FORM_ID} className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="role-form-name">
            Nome <span aria-hidden>*</span>
          </label>
          <InputText
            id="role-form-name"
            className={`${styles.input} ${nameError ? styles.inputInvalid : ""}`}
            value={nome}
            maxLength={NAME_MAX_LENGTH}
            placeholder={NAME_PLACEHOLDER}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setNome(event.currentTarget.value)}
            disabled={isSubmitting}
            required
            aria-invalid={Boolean(nameError)}
            aria-describedby={nameError ? "role-form-name-error" : undefined}
            data-testid="role-form-name"
          />
          {nameError ? (
            <span
              id="role-form-name-error"
              className={styles.fieldError}
              role="alert"
              data-testid="role-form-name-error"
            >
              {nameError}
            </span>
          ) : null}
        </div>

        <fieldset className={styles.permissions}>
          <label className={styles.legend}>Permissões</label>
          {renderPermissions()}
        </fieldset>
      </form>
    </Dialog>
  );
}
